import { 
    getLogger,
    PostConfigData, 
    CONNECTOR_MASTER_REQUEST_PROTOCOLS, 
    MaybeSystemMessage, 
    ReceivedMessage, 
    SystemRequest,
    tryParse 
} from "@gotti/core";
import { BaseServer, BaseServerConfig, RegisteredServerData, RemoveConnectionReason } from "../BaseServer.js";
import { ProtocolArray } from "../ServerConnector/BaseServerConnector.js";
import { AreaIdAndOptions, ServerGameFile, makeGameFileData, makeGameFileMetaData } from "../ServerGameFile.js";
import { PeerAreaData } from "../ServerProcess.js";
import { MASTER_AREA_PROTOCOLS, MASTER_GATE_PROTOCOLS, SERVER_SERVER_PROTOCOLS, SERVER_TYPES } from "../constants.js";
import { AreaServerConfigOptions } from "./AreaServer.js";
import { ConnectorServerConfigOptions } from "./ConnectorServer.js";
import { GateServerConfigOptions } from "./GateServer.js";
import { DynamicAreaTable, DYNAMIC_AREA_STATE, DynamicAreaPolicy, DynamicAreaRecord, memberCountOf } from "../dynamicAreas/DynamicAreaTable.js";
import type { MasterStateStore, DestroyIntentOpts } from "../state/MasterStateStore.js";
import { MemoryMasterStateStore } from "../state/MemoryMasterStateStore.js";
import { RedisMasterStateStore } from "../state/RedisMasterStateStore.js";
import { AreaTypeRegistry, type AreaTypeRegistrationOptions } from "../dynamicAreas/AreaTypeRegistry.js";
import { HostSnapshot, PlacementPolicy, SameProcessPlacement } from "../dynamicAreas/PlacementPolicy.js";
import { AreaHost, AreaHostLimits } from "../dynamicAreas/AreaHost.js";
import { HostReport } from "../dynamicAreas/AreaHostAgent.js";
import { MASTER_CONNECTOR_PROTOCOLS } from "../constants.js";
import { ServerAreaFile } from "../ServerAreaFile.js";
import { FsAssetStore, type AssetStore } from "../assets/AssetStore.js";
import type { AssetChange, AssetChangedBy, AssetNamespace, AssetAction } from "@gotti/core";
import { connectorScopeMatches, type ConnectorChangePayload } from "../types.js";
import * as PATH from "path";
import { randomBytes } from "crypto";
import { verifyJoinToken, JoinRole } from "../trust/joinToken.js";
import { isCurveKey } from "../trust/keys.js";

export type CreateDynamicAreaOptions = {
    gameId?: string,
    areaKey?: string,
    configData?: any,
    policy?: Partial<DynamicAreaPolicy>,
    requesterHostId?: string | number,
    /** Owner of the created area (area id, or 'master' for pipeline-auto-created). */
    createdBy?: string | number,
};

export type CreateDynamicAreaResult = { areaId: string, created: boolean };

/** Per-host + total capacity for one area type. `null` = uncapped/unlimited. */
export type AreaCapacityReport = {
    type: string,
    total: { running: number, capacity: number | null, available: number | null },
    byHost: Array<{ hostServerId: string | number, running: number, capacity: number | null, available: number | null }>,
};

export type MasterServerConfigOptions = {
    disableAdminWebServer?: boolean,
    httpServer?: any,
    webPort?: number,
    /** Root for the master-owned asset store (Unified Assets API). Default <cwd>/assets. */
    assets?: { path?: string },
    /**
     * Namespace every redis key this master's state store writes
     * (`<prefix>dyn:members:<areaId>`, `<prefix>intent:destroy`). Same field,
     * same default and same purpose as the gate's `redisKeyPrefix`: it lets a
     * redeploy start from a clean namespace in a shared redis without flushing
     * anything else. Ignored in the default memory mode.
     */
    redisKeyPrefix?: string,
}

/** Default namespace for a redis-backed master's keys — see `redisKeyPrefix`. */
export const DEFAULT_MASTER_REDIS_KEY_PREFIX = 'gotti:';

export const MASTER_ENDPOINTS = {
    AUTHENTICATE: '__gotti_master_authenticate'
}

export type MasterServerConfig = Omit<BaseServerConfig<MasterServerConfigOptions>, 'type'>

import * as http from 'http';

const { GATE, MASTER, CONNECTOR, AREA, HOST } = SERVER_TYPES;

export abstract class MasterServer extends BaseServer {
    readonly awaitingStartup : {[key: string]: { server: BaseServerConfig<any>, timeout: NodeJS.Timeout }} = {};

    private httpServer : any;

    readonly logger = getLogger("MasterServer:", { kind: 'master', serverId: this.id });

    readonly serversByType : {
        connector: Array<RegisteredServerData<ConnectorServerConfigOptions>>,
        areas: Array<RegisteredServerData<AreaServerConfigOptions>>,
        gate: RegisteredServerData<GateServerConfigOptions> | null
    } = {
        connector: [],
        areas: [],
        gate: null,
    }
    private debounceMap: Map<string, ReturnType<typeof setTimeout>> = new Map();
    private debounceCountMap: Map<string, number> = new Map();
    private MAX_DEBOUNCES = 5; // Adjust as needed
    private DEBOUNCE_DELAY = 300; // Adjust as needed
    
    readonly gameTypes : {[gameType: string]: ServerGameFile } = {};
    readonly gamesByType : {[gameType: string]: {[gameId: string]: PostConfigData }} = {}
    readonly gamesById: {[gameId: string]: PostConfigData } = {}
    readonly gameClientDataById: {[gameId: string]: any } = {};
    readonly areaByGameMap : {[gameId: string]: {[areaId: string]: RegisteredServerData<AreaServerConfigOptions> }} = {};    
    /**
     * Admin HTTP sessions: token → expiry (epoch ms). Tokens are 32 random bytes
     * (CSPRNG), live `adminTokenTtlMs` from issue, and are looked up in a Map —
     * never with `in` on an object, where `Authorization: toString` matched.
     * Private: they are bearer credentials (the Starter's meta used to print them).
     */
    private readonly adminSessions : Map<string, number> = new Map();
    /** Lifetime of an admin token from issue. Numeric tuning — there is no "never expires". */
    public adminTokenTtlMs = 60 * 60 * 1000;

    /** Live admin sessions (count only — the tokens never leave this object). */
    public get adminSessionCount(): number {
        this.pruneAdminSessions();
        return this.adminSessions.size;
    }

    private pruneAdminSessions(now = Date.now()) {
        this.adminSessions.forEach((expiresAt, token) => { if (expiresAt <= now) this.adminSessions.delete(token); });
    }

    /** `Authorization: Bearer <token>` (or the bare token) → a live session token, else undefined. */
    private adminSessionFor(header: unknown): string | undefined {
        if (typeof header !== 'string' || !header) return undefined;
        const token = header.startsWith('Bearer ') ? header.slice(7).trim() : header.trim();
        const expiresAt = this.adminSessions.get(token);
        if (expiresAt === undefined) return undefined;
        if (expiresAt <= Date.now()) {
            this.adminSessions.delete(token);
            return undefined;
        }
        return token;
    }

    readonly assetStore : AssetStore;

    readonly globalAreaServers : {[areaId: string]: RegisteredServerData<AreaServerConfigOptions> } = {}

    // ── Dynamic area instances (control-plane authority) ──────────────────────
    readonly dynamicAreaTable = new DynamicAreaTable({
        spillStore: () => this.memberSpillStore,
        onSpillError: (err) => this.logger.error(`Dynamic-area member spill write failed: ${err instanceof Error ? err.message : String(err)}`),
    });
    readonly areaTypes = new AreaTypeRegistry();
    placement : PlacementPolicy = new SameProcessPlacement();
    // Set by the host process (e.g. the Starter) to execute local spawn/destroy.
    areaHost? : AreaHost;
    // Remote instance hosts (SERVER_TYPES.HOST agents) keyed by their server id,
    // with the area types they advertise (kept fresh by HOST_REPORT_AREAS).
    readonly areaHostAgents : Map<string | number, { registeredTypes: string[], typeCapacities?: { [type: string]: number }, typeDefaults?: { [type: string]: { emptyTtlMs?: number, maxClients?: number } }, limits?: AreaHostLimits }> = new Map();
    // Hosts whose connection dropped: we keep re-preparing for their registration
    // so their reconnect loop can rejoin (cleared when they do).
    private readonly lostAreaHostConfigs : Map<string | number, BaseServerConfig<any>> = new Map();
    private readonly lostHostPrepareTimers : Map<string | number, ReturnType<typeof setTimeout>> = new Map();

    /** Keep a registration door open for a lost host until it rejoins. */
    private armPrepareForLostHost(hostServerId: string | number) {
        const config = this.lostAreaHostConfigs.get(hostServerId);
        if (!config) return;
        this.prepareForServerRegistrationRequest(config, { timeout: 30_000 })
            .then(() => {
                this.lostAreaHostConfigs.delete(hostServerId);
            })
            .catch(() => {
                if (!this.lostAreaHostConfigs.has(hostServerId)) return;
                const timer = setTimeout(() => {
                    this.lostHostPrepareTimers.delete(hostServerId);
                    this.armPrepareForLostHost(hostServerId);
                }, 1000);
                (timer as any).unref?.();
                this.lostHostPrepareTimers.set(hostServerId, timer);
            });
    }
    // Grace before an emptied ephemeral instance is torn down (per-instance override
    // via policy.emptyTtlMs). Numeric tuning of a live feature — never a disable.
    public defaultEmptyTtlMs = 10000;
    private readonly emptyReapTimers : Map<string, ReturnType<typeof setTimeout>> = new Map();
    // Upper bound on how long a destroyArea({ whenEmpty:true }) waits for the last
    // member to leave before forcing the teardown (per-call override via timeoutMs).
    public defaultDestroyWhenEmptyTimeoutMs = 30000;
    // Areas with an EXPLICIT deferred destroy pending. Only destroyArea ever adds
    // here — emptiness alone never arms a teardown for managed areas.
    private readonly pendingWhenEmptyDestroys : Map<string, { opts: DestroyIntentOpts, timer: ReturnType<typeof setTimeout> }> = new Map();

    // ── Master state store (members spill + durable destroy intents) ──────────

    /**
     * The memory backing, built once and kept — unlike `RedisMasterStateStore`
     * it owns state (the intents Map), so it must survive across `stateStore`
     * reads. Lazily constructed by the getter rather than as a field
     * initializer, mirroring `GateServer`.
     */
    private memoryStateStore? : MemoryMasterStateStore;

    /**
     * The redis store, memoized on the IDENTITY of the `this.redis` handle it
     * wraps. `RedisMasterStateStore` is stateless (a handle + a key prefix), so
     * re-using one is free of the mode-flip hazard the memory memoization has
     * to avoid — and keying on the handle means a master that (re)connects
     * redis (`initialize()` assigns `this.redis` AFTER construction) can never
     * keep a store pointing at a dead client.
     */
    private redisStateStore? : { redis: any, store: RedisMasterStateStore };

    /**
     * Every store read/write the master makes goes through here, so there is
     * exactly ONE redis-or-memory decision. Deliberately the same shape as
     * `GateServer.stateStore` — a memoized GETTER, resolved per access rather
     * than assigned at init: `this.redis` is created inside
     * `BaseServer.initialize()` (after `onBeforeInit`, before `onAfterInit`),
     * and most masters in tests never call `initialize()` at all. Memoization
     * lives ONLY in the memory arm, which is what makes the getter mode-flip
     * safe: touching it before redis exists cannot pin a master to memory.
     */
    public get stateStore() : MasterStateStore {
        if(this.redis) {
            if(this.redisStateStore?.redis !== this.redis) {
                this.redisStateStore = {
                    redis: this.redis,
                    store: new RedisMasterStateStore(this.redis, { keyPrefix: this.config.options?.redisKeyPrefix ?? DEFAULT_MASTER_REDIS_KEY_PREFIX }),
                };
            }
            return this.redisStateStore.store;
        }
        return (this.memoryStateStore = this.memoryStateStore ?? new MemoryMasterStateStore());
    }

    /**
     * Where the dynamic-area table spills its members — the store when there
     * is somewhere to spill TO, `undefined` otherwise.
     *
     * This is NOT a capability fork (both modes carry identical membership
     * semantics, and `getAreaMembers` reads members in either), it is the
     * storage-LOCATION choice redis mode exists to make: memory mode's "store"
     * is process memory, so routing the arrays through it would move the same
     * O(clients) bytes behind a promise and buy nothing, while costing memory
     * mode its byte-identical synchronous membership path. In redis mode the
     * ids leave the heap entirely and the record keeps only a count.
     */
    private get memberSpillStore() : MasterStateStore | undefined {
        return this.redis ? this.stateStore : undefined;
    }

    /**
     * Destroy intents read from the store at boot, held PENDING. An intent is
     * applied ONLY when its area actually re-registers (see
     * `claimRehydratedIntent`) — never on the strength of the stored row
     * alone, which is what keeps a dead deployment's leftovers from
     * resurrecting phantom areas. Never-claimed intents age out.
     */
    private readonly pendingRehydratedIntents : Map<string, { opts: DestroyIntentOpts, generation: number }> = new Map();
    private rehydratedIntentAgeOutTimer? : ReturnType<typeof setTimeout>;
    /**
     * How long a rehydrated intent waits for its area to re-register before it
     * is discarded from the pending map AND the store. Numeric tuning of a live
     * feature: the intent is always eventually resolved one way or the other,
     * this only sets how long "the host is still coming back" is believed.
     */
    public rehydratedIntentAgeOutMs = 60000;
    // Backpressure caps (undefined = unbounded). Enforced on create so client-driven
    // creation can't self-DoS the cluster.
    public maxAreasPerType? : number;
    public maxAreasPerHost? : number;

    readonly subscribedAreaStateUpdates : {[areaId: string]: Array<string> } = {};

    readonly serverIds : Array<string | number> = [];

    private allAreaIds : Array<string | number> = [];
    declare readonly config: BaseServerConfig<MasterServerConfigOptions>;

    constructor(c : MasterServerConfig) {
        super({ ...c, type: MASTER });
        if(c.inProcessOnly) {
            this.dispatchToAllAreas = this.dispatchToAllAreasLocalOnly.bind(this);
            this.dispatchToAreas = this.dispatchToAreasLocalOnly.bind(this);
        }
        if(c.options.httpServer) {
            this.httpServer = c.options.httpServer;
        }

        this.onConnectionMade((s) => {
            if(s.type === GATE) {
                this.serversByType.gate = s;
                Object.values(this.gamesByType).forEach(gameLookup => {
                    Object.values(gameLookup).forEach(gamePostConfig => {
                        // 6-slot payload: [proto, type, id, clientData, meta, authIdentityPath],
                        // exactly what addGame (below, and the gate's ADD_GAME case) reads.
                        // This replay previously omitted clientData entirely, so `meta` landed
                        // in the clientData slot and every game replayed to a (re)connecting
                        // gate arrived with meta === undefined and clientData === the meta.
                        this.sendToServer(this.serversByType.gate.id, [
                            MASTER_GATE_PROTOCOLS.ADD_GAME,
                            gamePostConfig.type,
                            gamePostConfig.id,
                            this.gameClientDataById[gamePostConfig.id],
                            gamePostConfig.meta,
                            this.gameTypes[gamePostConfig.type]?.authIdentityPath,
                        ])
                    })
                });
            } else if (s.type === AREA) {
                this.serversByType.areas.push(s);
                this.refreshAreaIds();
                if(s.options.gameId) {
                    this.addGameArea(s.options.gameId, s.id);
                } else {
                    this.addGlobalArea(s);
                }
                // A rolled-back spawn completing late registers itself like any
                // area — recognize the zombie immediately and destroy it.
                if(this.rolledBackAreas.has(s.id as string)) {
                    this.reapZombieArea(s.id as string);
                }
            } else if (s.type === CONNECTOR) {
                this.serversByType.connector.push(s);
                this.broadcastConnectorChange({ op: 'added', connectorId: s.id, config: s });
            } else if (s.type === HOST) {
                if(!this.areaHostAgents.has(s.id)) {
                    this.areaHostAgents.set(s.id, { registeredTypes: [] });
                }
                // The host's report (types + live instances + limits) rides in on
                // the registration handshake as its heartbeat state — consume it
                // directly so a (re)connect populates types/reconciles even if the
                // agent's own follow-up report message is lost (re-registration).
                const report = s.heartbeats?.lastState;
                if(report && Array.isArray(report.registeredTypes)) {
                    this.handleHostReport(s.id, report);
                }
                // (Re)joined — stop any re-prepare loop for it.
                this.lostAreaHostConfigs.delete(s.id);
                const pendingPrepare = this.lostHostPrepareTimers.get(s.id);
                if(pendingPrepare) {
                    clearTimeout(pendingPrepare);
                    this.lostHostPrepareTimers.delete(s.id);
                }
            } else {
                throw new Error(`Invalid server type ${s.type}`);
            }
            if(!this.serverIds.includes(s.id)) {
                this.serverIds.push(s.id);
            }
        });
        this.onConnectionClosed((s, reason) => {
            if(s.type === GATE) {
                this.serversByType.gate = null;
            } else if (s.type === HOST) {
                // Host lost (explicit disconnect or missed heartbeat) — reap every
                // instance it was running; its areas are unreachable. Keep a
                // prepared door open so the host's own reconnect loop can rejoin.
                this.areaHostAgents.delete(s.id);
                this.reapHostAreas(s.id, reason);
                // The door is pinned to the host's RECORDED key (and cluster): only
                // the same identity can walk back in, never whoever claims its id.
                this.lostAreaHostConfigs.set(s.id, { id: s.id, host: s.host, port: s.port, type: s.type, options: s.options ?? {}, publicKey: s.publicKey, clusterId: s.clusterId });
                this.armPrepareForLostHost(s.id);
            } else {
                const serverType = s.type as unknown as keyof typeof this.serversByType;
                if (serverType !== 'gate' && Array.isArray(this.serversByType[serverType])) {
                    const serverArray = this.serversByType[serverType] as RegisteredServerData<any>[];
                    const idx = serverArray.findIndex((i: RegisteredServerData<any>) => i.id === s.id);
                    if (idx > -1) {
                        serverArray.splice(idx, 1);
                    }
                }
                if(s.type === AREA) {
                    this.refreshAreaIds();
                    // A dynamic instance's own connection died (host may still be
                    // alive) — its record must not outlive the area.
                    const record = this.dynamicAreaTable.get(s.id as string);
                    if(record) {
                        this.cancelEmptyReap(record.areaId);
                        // The area is gone, so a pending whenEmpty destroy has
                        // nothing left to wait for: drop its timer AND its
                        // durable intent, or the store accumulates a row for an
                        // area that no longer exists (the intent's own age-out
                        // would only ever reach it after a master restart).
                        this.cancelPendingWhenEmptyDestroy(record.areaId);
                        try { this.dynamicAreaTable.markClosed(record.areaId); } catch { /* mid-teardown */ }
                        this.removeAreaFromTopology(record.areaId, record.gameId, true);
                        this.syncInstances();
                    }
                } else if(s.type === CONNECTOR) {
                    this.broadcastConnectorChange({ op: 'removed', connectorId: s.id });
                }
            }
            if(reason === RemoveConnectionReason.MISSED_HEARTBEAT) {
                console.error(`Server ${s.id} missed heartbeat, removing connection.`);
            }
            const idx = this.serverIds.indexOf(s.id);
            if(idx > -1) {
                this.serverIds.splice(idx, 1);
            }
        });
        this.assetStore = new FsAssetStore(c.options?.assets?.path ?? PATH.join(process.cwd(), 'assets'));
    }

    public addGlobalArea(data: RegisteredServerData<AreaServerConfigOptions>) {
        this.globalAreaServers[data.id] = data;
        this.syncGlobalAreas();
    }

    public removeGlobalArea(data: RegisteredServerData<AreaServerConfigOptions>) {
        if(data.id in this.globalAreaServers) {
            delete this.globalAreaServers[data.id];
            this.syncGlobalAreas();
        }
    }

    /**
     * Connector topology fan-out: every live area + the gate (if connected)
     * is notified of a connector coming up, draining, or going away, so each
     * can react independently (areas: detach affected clients; gate: stop
     * matchmaking new seats onto it) without polling. Wired from the
     * onConnectionMade/onConnectionClosed CONNECTOR branches below; 'draining'
     * is emitted by the runtime connector-removal path (removeConnectorAtRuntime).
     */
    public broadcastConnectorChange(payload: ConnectorChangePayload) {
        for (const a of this.serversByType.areas) {
            this.sendToServer(a.id, [MASTER_AREA_PROTOCOLS.CONNECTOR_CHANGE, payload]);
        }
        if (this.serversByType.gate) {
            this.sendToServer(this.serversByType.gate.id, [MASTER_GATE_PROTOCOLS.CONNECTOR_CHANGE, payload]);
        }
    }

    public addSubscribedArea(areaId: string, toArea: string) {
        throw new Error(`Unimplemented`)
    }

    // ── Dynamic area instances ────────────────────────────────────────────────

    /** Register an area *type* (template) that instances can be created from. */
    public registerAreaType(areaFile: ServerAreaFile, opts?: AreaTypeRegistrationOptions) {
        return this.areaTypes.register(areaFile, opts);
    }

    /**
     * Swap a registered type's area file (hot reload of its module): instances
     * this master spawns from now on run `areaFile`. Live instances are not
     * touched (reload those through their AreaServer).
     */
    public replaceAreaType(type: string, areaFile: ServerAreaFile, opts?: { sourcePath?: string }) {
        return this.areaTypes.replace(type, areaFile, opts);
    }

    /**
     * A type's default instance policy (`emptyTtlMs` / `maxClients`). Resolved
     * from the SAME host snapshot as every other per-type datum (registeredTypes,
     * typeCapacities, limits) — the master's co-located host and each remote host
     * each contribute their declared defaults, so a type registered only on a
     * remote host still carries its defaults with no special case.
     *
     * Defaults are a property of the TYPE, so every host advertising it reports
     * an identical value; we take the first in the snapshot's stable order (the
     * master's own co-located host precedes remote agents, so a locally-declared
     * default wins) and warn if a deployment disagrees rather than silently
     * picking one.
     */
    private effectiveTypeDefaults(type: string, hosts: HostSnapshot[] = this.getAreaHostsSnapshot()): { emptyTtlMs?: number, maxClients?: number } {
        const declared = hosts
            .filter(h => h.registeredTypes.includes(type))
            .map(h => h.typeDefaults?.[type])
            .filter((d): d is { emptyTtlMs?: number, maxClients?: number } => d !== undefined);
        if (!declared.length) return {};
        const chosen = declared[0];
        if (declared.some(d => d.emptyTtlMs !== chosen.emptyTtlMs || d.maxClients !== chosen.maxClients)) {
            this.logger.warn(`Area hosts disagree on the default policy for area type "${type}"; using ${JSON.stringify(chosen)}.`);
        }
        return chosen;
    }

    public getDynamicArea(areaId: string): DynamicAreaRecord | undefined {
        return this.dynamicAreaTable.get(areaId);
    }

    public listDynamicAreas(filter?: { type?: string, gameId?: string }): DynamicAreaRecord[] {
        return this.dynamicAreaTable.list(filter);
    }

    /** Every process that can spawn an instance right now: the co-located host + remote HOST agents. */
    public getAreaHostsSnapshot(): HostSnapshot[] {
        const counts: { [hostId: string]: number } = {};
        const members: { [hostId: string]: number } = {};
        this.dynamicAreaTable.list().forEach(r => {
            if (r.hostServerId !== undefined) {
                counts[r.hostServerId] = (counts[r.hostServerId] ?? 0) + 1;
                members[r.hostServerId] = (members[r.hostServerId] ?? 0) + memberCountOf(r);
            }
        });
        const hosts: HostSnapshot[] = [];
        if (this.areaHost) {
            hosts.push({
                serverId: this.areaHost.hostServerId,
                registeredTypes: this.areaHost.registeredTypes(),
                areaCount: counts[this.areaHost.hostServerId] ?? 0,
                memberCount: members[this.areaHost.hostServerId] ?? 0,
                typeCapacities: this.areaHost.typeCapacities?.() ?? {},
                typeDefaults: this.areaHost.typeDefaults?.() ?? {},
                limits: this.areaHost.hostLimits?.() ?? {},
            });
        }
        this.areaHostAgents.forEach((agent, serverId) => {
            hosts.push({
                serverId,
                registeredTypes: agent.registeredTypes,
                areaCount: counts[serverId] ?? 0,
                memberCount: members[serverId] ?? 0,
                typeCapacities: agent.typeCapacities ?? {},
                typeDefaults: agent.typeDefaults ?? {},
                limits: agent.limits ?? {},
            });
        });
        return hosts;
    }

    /**
     * Capacity for `type` right now — how many are running, the declared cap, and
     * how many more will fit, per host and in total. The matchmaking primitive:
     * "how many dungeons can I still start, and where?". Uncapped is reported as
     * `null` (unlimited) — explicit and JSON-safe over the wire, unlike Infinity.
     */
    public getAreaCapacity(type: string): AreaCapacityReport {
        const running: { [hostId: string]: number } = {};
        this.dynamicAreaTable.list({ type }).forEach(r => {
            if (r.hostServerId !== undefined) {
                running[r.hostServerId] = (running[r.hostServerId] ?? 0) + 1;
            }
        });
        const byHost = this.getAreaHostsSnapshot()
            .filter(h => h.registeredTypes.includes(type))
            .map(h => {
                const capacity = h.typeCapacities?.[type] ?? null;
                const run = running[h.serverId] ?? 0;
                const available = capacity === null ? null : Math.max(0, capacity - run);
                return { hostServerId: h.serverId, running: run, capacity, available };
            });
        const anyUnlimited = byHost.some(h => h.capacity === null);
        const total = {
            running: byHost.reduce((n, h) => n + h.running, 0),
            capacity: anyUnlimited ? null : byHost.reduce((n, h) => n + (h.capacity as number), 0),
            available: anyUnlimited ? null : byHost.reduce((n, h) => n + (h.available as number), 0),
        };
        return { type, total, byHost };
    }

    // In-flight keyed creates — concurrent same-key callers coalesce onto one
    // spawn instead of racing (and all see its failure if it fails).
    private readonly pendingCreatesByKey : Map<string, Promise<CreateDynamicAreaResult>> = new Map();

    // Tombstones for remote spawns WE rolled back (e.g. request timeout) but the
    // host may still complete: the id must never be re-adopted from a host
    // report, and any sign of it (report / area registration) triggers a destroy.
    private readonly rolledBackAreas : Map<string, { hostServerId?: string | number, gameId?: string, ts: number }> = new Map();
    private tombstoneRolledBackArea(record: DynamicAreaRecord) {
        const now = Date.now();
        this.rolledBackAreas.forEach((t, id) => {
            if (now - t.ts > 10 * 60_000) this.rolledBackAreas.delete(id);
        });
        this.rolledBackAreas.set(record.areaId, { hostServerId: record.hostServerId, gameId: record.gameId, ts: now });
    }

    /**
     * A rolled-back spawn surfaced alive on its host (late completion): kill it
     * there and scrub any topology it managed to register. The tombstone is kept
     * until the destroy acks so repeated reports keep retrying.
     */
    private reapZombieArea(areaId: string) {
        const tombstone = this.rolledBackAreas.get(areaId);
        if (!tombstone) return;
        this.logger.error(`Rolled-back dynamic area ${areaId} surfaced on host ${tombstone.hostServerId} — destroying the zombie.`);
        this.removeAreaFromTopology(areaId, tombstone.gameId, true);
        if (tombstone.hostServerId !== undefined && this.areaHostAgents.has(tombstone.hostServerId)) {
            // Keep the tombstone (TTL-purged) even after an ack: an ack can race a
            // mid-spawn zombie, and every later report/registration retries the
            // destroy until the host truly stops surfacing the id.
            this.requestServer(tombstone.hostServerId, [MASTER_AREA_PROTOCOLS.TEARDOWN_AREA, areaId, false])
                .catch(err => this.logger.error(`Failed destroying zombie instance ${areaId}: ${err instanceof Error ? err.message : String(err)}`));
        }
    }

    /**
     * A keyed instance existing players can still be routed into: READY, not
     * draining, and not full (`policy.maxClients`). A full room overflows —
     * create-or-join re-points the key at a fresh instance.
     */
    private getJoinableKeyedArea(areaKey: string): DynamicAreaRecord | undefined {
        const existing = this.dynamicAreaTable.getByKey(areaKey);
        if (!existing) return undefined;
        if (existing.state !== DYNAMIC_AREA_STATE.READY) return undefined;
        if (existing.policy.maxClients !== undefined && memberCountOf(existing) >= existing.policy.maxClients) return undefined;
        return existing;
    }

    /**
     * Create a fresh area instance from a registered type, scoped to its own
     * `configData`. Idempotent on `areaKey` (create-or-join): a joinable
     * keyed instance is returned as-is; a DRAINING or FULL one overflows to a
     * fresh instance; concurrent same-key calls coalesce onto a single spawn.
     * The Master is the authority for the record; execution is placed on the
     * co-located host (direct-call fast path) or a remote HOST agent (real
     * registration handshake over the wire), whichever the placement policy
     * chooses among admitted hosts.
     */
    public async createOrJoinArea(type: string, opts: CreateDynamicAreaOptions = {}): Promise<CreateDynamicAreaResult> {
        const key = opts.areaKey;
        if (!key) {
            return this.doCreateOrJoinArea(type, opts);
        }
        const inFlight = this.pendingCreatesByKey.get(key);
        if (inFlight) {
            const settled = await inFlight; // a failure rejects every coalesced caller
            return { areaId: settled.areaId, created: false };
        }
        const joinable = this.getJoinableKeyedArea(key);
        if (joinable) {
            return { areaId: joinable.areaId, created: false };
        }
        const create = this.doCreateOrJoinArea(type, opts);
        this.pendingCreatesByKey.set(key, create);
        try {
            return await create;
        } finally {
            this.pendingCreatesByKey.delete(key);
        }
    }

    // ── Author-facing dynamic-area API (area terms — no "instance" verbiage) ───

    /**
     * Explicit CREATE of a dynamic area (`system.createArea` / `master.createArea`).
     * Unlike the create-OR-join join path, this ASSERTS a new area: if a live area
     * for `(type, key)` already exists it throws. `createdBy` records the owner so
     * only the creator (or a master) may later `destroyArea` it.
     */
    public async createArea(type: string, opts: { key?: string, gameId?: string, configData?: any, managed?: boolean, maxClients?: number, emptyTtlMs?: number, createdBy?: string | number, requesterHostId?: string | number } = {}): Promise<{ areaId: string }> {
        if (opts.key) {
            const existing = this.dynamicAreaTable.getByKey(opts.key);
            // A DRAINING record is committed to destruction (a pending
            // destroyArea({ whenEmpty }) owns it) — an explicit create for the
            // same key must not be blocked for the drain window. The create
            // re-points byKey to the fresh area; the old one drains out and
            // closes under its pending destroy.
            if (existing && existing.type === type &&
                existing.state !== DYNAMIC_AREA_STATE.CLOSED &&
                existing.state !== DYNAMIC_AREA_STATE.DRAINING) {
                throw new Error(`Area of type "${type}" with key "${opts.key}" already exists.`);
            }
        }
        const { areaId } = await this.doCreateOrJoinArea(type, {
            gameId: opts.gameId,
            areaKey: opts.key,
            configData: opts.configData,
            createdBy: opts.createdBy ?? 'master',
            requesterHostId: opts.requesterHostId,
            // `managed` (or emptyTtlMs:0) opts out of empty-self-reap → a persistent area.
            policy: {
                ephemeral: opts.managed ? false : true,
                maxClients: opts.maxClients,
                emptyTtlMs: opts.emptyTtlMs,
            },
        });
        return { areaId: areaId };
    }

    /**
     * Tear down a dynamic area (`system.destroyArea` / `master.destroyArea`).
     * Honored only from the OWNER (the area that created it) or the master's own
     * code (`requesterId` omitted — a programmatic call in this process); anyone
     * else is rejected. Master rights are NEVER granted by a requester id string:
     * an area that happens to be named `'master'` is just an area.
     *
     * `whenEmpty: true` defers the teardown until the LAST member leaves,
     * bounded by `timeoutMs` (default `defaultDestroyWhenEmptyTimeoutMs`).
     * The record goes DRAINING immediately so create-or-join stops handing the
     * area out, and the drain hook runs at the final teardown. This is strictly
     * opt-in per call — an area that merely becomes empty is never torn down
     * unless its policy is ephemeral (the pre-existing emptyTtl reap) or a
     * destroyArea call armed it.
     */
    public async destroyArea(areaId: string, opts: { drain?: boolean, whenEmpty?: boolean, timeoutMs?: number, reason?: string, requesterId?: string | number } = {}): Promise<void> {
        const record = this.dynamicAreaTable.get(areaId);
        if (!record) return;
        const isMaster = opts.requesterId === undefined;
        if (!isMaster && record.createdBy !== opts.requesterId) {
            throw new Error(`Area ${areaId} can only be destroyed by its creator (${String(record.createdBy)}) or a master.`);
        }
        if (opts.whenEmpty && memberCountOf(record) > 0) {
            if (record.state === DYNAMIC_AREA_STATE.READY) {
                this.dynamicAreaTable.markDraining(areaId);
            }
            // The explicit destroy supersedes any ephemeral empty-grace timer.
            this.cancelEmptyReap(areaId);
            // TIMER ONLY, deliberately: re-arming must never pass through a
            // state where the durable intent is absent. `putDestroyIntent`
            // below is last-write-wins, so clearing first would buy nothing and
            // would open a window in which a master crash loses an intent that
            // was never actually cancelled.
            this.cancelPendingWhenEmptyDestroyTimer(areaId);
            // whenEmpty PROMISES the drain hook at the final teardown — default
            // drain on. Without the explicit flag the remote host would skip
            // runDrainHook (TEARDOWN_AREA is flag-driven; only the co-located
            // path keys off the DRAINING record state).
            const finalOpts = { drain: opts.drain ?? true, reason: opts.reason };
            const timer = setTimeout(() => {
                this.pendingWhenEmptyDestroys.delete(areaId);
                this.tearDownDynamicArea(areaId, { drain: finalOpts.drain, reason: finalOpts.reason ?? 'when-empty-timeout' }).catch(err => {
                    this.logger.error(`Failed forced when-empty teardown of dynamic area ${areaId}: ${err instanceof Error ? err.message : String(err)}`);
                });
            }, opts.timeoutMs ?? this.defaultDestroyWhenEmptyTimeoutMs);
            (timer as any).unref?.();
            this.pendingWhenEmptyDestroys.set(areaId, { opts: finalOpts, timer });
            // DURABLE half of the same arming. The in-process timer above is
            // the exact mechanism; this is the fact that survives the process —
            // the one piece of master state no live server can re-teach, since
            // the area, its host and its members all re-register themselves but
            // "someone asked me to tear this down when it empties" exists
            // nowhere else. The generation pins it to THIS incarnation of the
            // area id.
            await this.stateStore.putDestroyIntent(areaId, { opts: finalOpts, generation: record.generation });
            return;
        }
        await this.tearDownDynamicArea(areaId, { drain: opts.drain, reason: opts.reason });
    }

    /**
     * Disarm a pending `whenEmpty` destroy — the in-process timer AND the
     * durable intent. The store clear is UNCONDITIONAL (an `HDEL` of a missing
     * field is a no-op) rather than gated on a live `pendingWhenEmptyDestroys`
     * entry, because the paths that reach here with no entry are exactly the
     * ones that must still clean up: the fire-and-complete path deletes its map
     * entry before tearing down, and a teardown for any other reason (host
     * reaped, area connection lost, spawn rolled back) never had one.
     */
    private cancelPendingWhenEmptyDestroy(areaId: string): Promise<void> {
        this.cancelPendingWhenEmptyDestroyTimer(areaId);
        // A rehydrated-but-unclaimed intent for this area is settled too — the
        // area is being torn down, so there is nothing left for it to apply to.
        this.pendingRehydratedIntents.delete(areaId);
        return this.stateStore.clearDestroyIntent(areaId).catch(err => {
            this.logger.error(`Failed clearing destroy intent for dynamic area ${areaId}: ${err instanceof Error ? err.message : String(err)}`);
        });
    }

    /** Disarm only the in-process timer — the durable intent is untouched. */
    private cancelPendingWhenEmptyDestroyTimer(areaId: string) {
        const pending = this.pendingWhenEmptyDestroys.get(areaId);
        if (pending) {
            clearTimeout(pending.timer);
            this.pendingWhenEmptyDestroys.delete(areaId);
        }
    }

    /** List live dynamic areas (optionally filtered by type / gameId). */
    public listAreas(filter?: { type?: string, gameId?: string }): DynamicAreaRecord[] {
        return this.dynamicAreaTable.list(filter);
    }

    private async doCreateOrJoinArea(type: string, opts: CreateDynamicAreaOptions = {}): Promise<CreateDynamicAreaResult> {
        const allHosts = this.getAreaHostsSnapshot();
        if (!allHosts.length) {
            throw new Error(`Cannot create dynamic area: no area host is configured on the master.`);
        }
        const registration = this.areaTypes.get(type);
        if (!registration && !allHosts.some(h => h.registeredTypes.includes(type))) {
            throw new Error(`Cannot create dynamic area: area type "${type}" is not registered on any host.`);
        }
        if (this.maxAreasPerType !== undefined &&
            this.dynamicAreaTable.list({ type }).length >= this.maxAreasPerType) {
            throw new Error(`Dynamic-area quota reached: max ${this.maxAreasPerType} areas of type "${type}".`);
        }
        // Automatic config-driven admission: a host is eligible when it (a) can run
        // the type, (b) is under the global per-host quota, (c) has a free per-type
        // slot (maxConcurrent; null = uncapped), and (d) fits its own host-level
        // totals from config (maxTotalAreas / maxTotalClients, counting live
        // players + the incoming instance's policy.maxClients). If every capable
        // host is full, this is a capacity error.
        const capacityByHost = new Map<string | number, number | null>(
            this.getAreaCapacity(type).byHost.map(h => [h.hostServerId, h.available]),
        );
        // Effective instance policy: per-instance opts.policy overrides the type's
        // declared defaults (emptyTtlMs/maxClients); emptyTtlMs still falls back to
        // master.defaultEmptyTtlMs at reap time when neither is set. Type
        // defaults are resolved uniformly from the host snapshot (co-located or
        // remote-only) — see effectiveTypeDefaults.
        const typeDefaults = this.effectiveTypeDefaults(type, allHosts);
        const mergedPolicy: Partial<DynamicAreaPolicy> = {
            ephemeral: opts.policy?.ephemeral,
            emptyTtlMs: opts.policy?.emptyTtlMs ?? typeDefaults.emptyTtlMs,
            maxClients: opts.policy?.maxClients ?? typeDefaults.maxClients,
        };
        const incomingClients = mergedPolicy.maxClients ?? 0;
        const hosts = allHosts.filter(h => {
            if (!h.registeredTypes.includes(type)) return false;
            if (this.maxAreasPerHost !== undefined && (h.areaCount ?? 0) >= this.maxAreasPerHost) return false;
            const available = capacityByHost.get(h.serverId);
            if (available !== undefined && available !== null && available <= 0) return false;
            if (h.limits?.maxTotalAreas !== undefined && (h.areaCount ?? 0) >= h.limits.maxTotalAreas) return false;
            if (h.limits?.maxTotalClients !== undefined && (h.memberCount ?? 0) + incomingClients > h.limits.maxTotalClients) return false;
            return true;
        });
        if (!hosts.length) {
            throw new Error(`Dynamic-area capacity reached for type "${type}": every capable host is at its concurrency/player limit.`);
        }
        const record = this.dynamicAreaTable.create({
            type,
            gameId: opts.gameId ?? registration?.gameId,
            areaKey: opts.areaKey,
            configData: opts.configData,
            policy: mergedPolicy,
            createdBy: opts.createdBy,
        });
        try {
            // Admission is config-driven: the `hosts` filter above already applied
            // per-type maxConcurrent + host-level dynamicAreaHost limits. Placement
            // picks among the admitted hosts.
            const hostServerId = await this.placement.choose(
                { type, requesterHostId: opts.requesterHostId, gameId: record.gameId },
                hosts,
            );
            record.hostServerId = hostServerId;
            if (this.areaHost && hostServerId === this.areaHost.hostServerId) {
                if (!registration) {
                    throw new Error(`Cannot spawn dynamic area locally: area type "${type}" is not registered on the master's host.`);
                }
                await this.areaHost.spawn(record, registration);
            } else {
                // Remote spawn: the agent constructs + wires the AreaServer, driving
                // the AREA_PREPARE choreography back through us.
                await this.requestServer(hostServerId, [MASTER_AREA_PROTOCOLS.SPAWN_AREA, {
                    areaId: record.areaId,
                    type: record.type,
                    gameId: record.gameId,
                    areaKey: record.areaKey,
                    configData: record.configData,
                    generation: record.generation,
                    policy: record.policy,
                }]);
            }
            this.dynamicAreaTable.markReady(record.areaId, { hostServerId });
            // Start the empty-grace timer at birth: an ephemeral instance nobody
            // ever joins (e.g. the requesting client disconnected mid-flow) must
            // not leak. The first join cancels it.
            if (record.policy.ephemeral) {
                this.scheduleEmptyReap(record);
            }
            this.syncInstances();
            return { areaId: record.areaId, created: true };
        } catch (err) {
            // Roll back the pending record so a failed spawn leaves nothing behind.
            try { this.dynamicAreaTable.markClosed(record.areaId); } catch { /* already gone */ }
            // A remote dispatch may have gone through even though we gave up (e.g.
            // request timeout) — tombstone the id so a late-completing zombie is
            // recognized and destroyed instead of re-adopted.
            const wasRemoteDispatch = record.hostServerId !== undefined &&
                (!this.areaHost || record.hostServerId !== this.areaHost.hostServerId);
            if (wasRemoteDispatch) {
                this.tombstoneRolledBackArea(record);
            }
            throw err;
        }
    }

    /**
     * Report that a client joined/left an instance. Drives ref-counted auto-empty
     * teardown: the last member leaving starts an `emptyTtlMs` grace timer; a
     * re-join before it fires cancels the teardown.
     *
     * `clientId` is typed `string | number` and normalized because this is a
     * WIRE entry point (`MASTER_AREA_PROTOCOLS.AREA_MEMBERSHIP`, payload[1]):
     * the sender is normalized too (`AreaServer.reportAreaMembership`), but the
     * membership list is a `string[]` matched with `indexOf`, so a single raw
     * numeric id reaching it silently makes an area un-emptiable — and the
     * master cannot vet the build on the other end of the socket.
     */
    public notifyAreaMembership(areaId: string, clientId: string | number, joined: boolean) {
        const record = this.dynamicAreaTable.get(areaId);
        if (!record) return;
        const memberId = String(clientId);
        if (joined) {
            this.dynamicAreaTable.addMember(areaId, memberId);
            this.cancelEmptyReap(areaId);
        } else {
            const { empty, settled } = this.dynamicAreaTable.removeMember(areaId, memberId);
            if (empty) {
                this.handleAreaEmptied(areaId);
            } else if (settled) {
                // SPILL mode only: the exact answer is the store's, one round
                // trip later (see DynamicAreaTable.removeMember for why a local
                // counter is not allowed to answer this). Memory mode never
                // takes this branch and stays synchronous, byte for byte.
                settled.then(r => { if (r.empty) this.handleAreaEmptied(areaId); });
            }
        }
        this.syncInstances();
    }

    /**
     * The last member left `areaId`: complete an armed `destroyArea({
     * whenEmpty })` or, for an ephemeral area, start its empty-grace timer.
     * Re-reads the record because in spill mode this can run a round trip after
     * the report that emptied the area.
     */
    private handleAreaEmptied(areaId: string) {
        const record = this.dynamicAreaTable.get(areaId);
        if (!record) return;
        // Re-check the (store-reconciled) count: in spill mode a JOIN can land
        // between the leave that emptied the area and this callback, and an
        // area somebody just joined must not be torn down. A no-op in memory
        // mode, where `empty` was derived from this same record synchronously.
        if (memberCountOf(record) > 0) return;
        const pending = this.pendingWhenEmptyDestroys.get(areaId);
        if (pending) {
            // An explicit destroyArea({ whenEmpty }) was armed — complete it now.
            this.cancelPendingWhenEmptyDestroy(areaId);
            this.tearDownDynamicArea(areaId, { drain: pending.opts.drain, reason: pending.opts.reason ?? 'when-empty' }).catch(err => {
                this.logger.error(`Failed when-empty teardown of dynamic area ${areaId}: ${err instanceof Error ? err.message : String(err)}`);
            });
        } else if (record.policy.ephemeral) {
            this.scheduleEmptyReap(record);
        }
    }

    /**
     * The clients currently in a dynamic area, in EITHER mode — the record's
     * own array in memory mode, the members spill in redis mode (where the
     * record deliberately does not retain the ids). The spill is rebuilt from
     * host reports on adoption and is never consulted to decide that an area
     * exists; `getDynamicArea`/`listAreas` remain the only answer to that.
     */
    public getAreaMembers(areaId: string): Promise<string[]> {
        return this.dynamicAreaTable.getMembers(areaId);
    }

    private scheduleEmptyReap(record: DynamicAreaRecord) {
        this.cancelEmptyReap(record.areaId);
        const ttl = record.policy.emptyTtlMs ?? this.defaultEmptyTtlMs;
        const timer = setTimeout(() => {
            this.emptyReapTimers.delete(record.areaId);
            const current = this.dynamicAreaTable.get(record.areaId);
            if (current && memberCountOf(current) === 0) {
                this.tearDownDynamicArea(record.areaId, { reason: 'empty' }).catch(err => {
                    this.logger.error(`Failed to reap empty dynamic area ${record.areaId}: ${err instanceof Error ? err.message : String(err)}`);
                });
            }
        }, ttl);
        // A pending reap timer must not keep the process alive.
        (timer as any).unref?.();
        this.emptyReapTimers.set(record.areaId, timer);
    }

    private cancelEmptyReap(areaId: string) {
        const timer = this.emptyReapTimers.get(areaId);
        if (timer) {
            clearTimeout(timer);
            this.emptyReapTimers.delete(areaId);
        }
    }

    /**
     * Tear down an instance and remove it from the table. Safe to call twice.
     * With `drain:true` the instance passes through DRAINING (so game code's
     * `onDrain` can migrate/kick players) before it closes.
     */
    public async tearDownDynamicArea(areaId: string, opts: { drain?: boolean, reason?: string } = {}): Promise<void> {
        const record = this.dynamicAreaTable.get(areaId);
        if (!record) return;
        this.cancelEmptyReap(areaId);
        this.cancelPendingWhenEmptyDestroy(areaId);
        if (opts.drain && record.state === DYNAMIC_AREA_STATE.READY) {
            this.dynamicAreaTable.markDraining(areaId);
        }
        const isLocal = this.areaHost && record.hostServerId === this.areaHost.hostServerId;
        if (isLocal) {
            await this.areaHost.destroy(record);
        } else if (record.hostServerId !== undefined && this.areaHostAgents.has(record.hostServerId)) {
            await this.requestServer(record.hostServerId, [MASTER_AREA_PROTOCOLS.TEARDOWN_AREA, areaId, !!opts.drain]);
        }
        try { this.dynamicAreaTable.markClosed(areaId); } catch { /* closed concurrently */ }
        this.removeAreaFromTopology(areaId, record.gameId, !isLocal);
        this.syncInstances();
    }

    /**
     * Make the master's area topology truthful after an instance is gone:
     * serversByType.areas / allAreaIds / game+global area maps / connection
     * bookkeeping, and (for remote instances) tell the connectors to drop the
     * area connection now instead of waiting for heartbeat timeouts.
     */
    private removeAreaFromTopology(areaId: string, gameId?: string, remote?: boolean) {
        const idx = this.serversByType.areas.findIndex(a => a.id === areaId);
        if (idx > -1) {
            this.serversByType.areas.splice(idx, 1);
        }
        this.refreshAreaIds();
        if (gameId && this.areaByGameMap[gameId]?.[areaId]) {
            this.removeGameArea(gameId, areaId);
        }
        if (this.globalAreaServers[areaId]) {
            this.removeGlobalArea(this.globalAreaServers[areaId]);
        }
        if (remote) {
            this.serversByType.connector.forEach(c => {
                this.sendToServer(c.id, [MASTER_CONNECTOR_PROTOCOLS.REMOVE_INSTANCE_AREA, areaId]);
            });
            const areaConnection = this.serverConnections[areaId];
            if (areaConnection) {
                this.disconnectServer(areaConnection).catch(err => {
                    this.logger.error(`Failed to drop connection for remote instance ${areaId}: ${err instanceof Error ? err.message : String(err)}`);
                });
            }
        } else {
            // Local instances were wired via addLocalServer (removeLocalServer is the
            // Starter's job); drop our connection bookkeeping without the pubSub path.
            const fIdx = this.finalizedConnections.indexOf(areaId);
            if (fIdx > -1) {
                this.finalizedConnections.splice(fIdx, 1);
            }
            delete this.serverConnections[areaId];
        }
    }

    /**
     * A remote host vanished (missed heartbeat or explicit disconnect): every
     * instance it ran is unreachable — close them out and clean topology. This is
     * the lease-expiry path; it shares all its machinery with normal teardown.
     */
    public reapHostAreas(hostServerId: string | number, reason?: RemoveConnectionReason) {
        const lost = this.dynamicAreaTable.list().filter(r => r.hostServerId === hostServerId);
        lost.forEach(record => {
            this.cancelEmptyReap(record.areaId);
            // Same reasoning as the area-connection-lost path: the instance is
            // unreachable, so its pending whenEmpty destroy — timer and durable
            // intent alike — is settled here rather than left to accumulate.
            this.cancelPendingWhenEmptyDestroy(record.areaId);
            try { this.dynamicAreaTable.markClosed(record.areaId); } catch { /* already closed */ }
            this.removeAreaFromTopology(record.areaId, record.gameId, true);
        });
        if (lost.length) {
            this.logger.error(`Instance host ${hostServerId} lost (${reason ?? 'disconnect'}); reaped ${lost.length} instance(s): ${lost.map(r => r.areaId).join(', ')}`);
            this.syncInstances();
        }
    }

    /**
     * A host agent is about to register a freshly-spawned instance AreaServer:
     * prepare ourselves and every connector for its registration handshake (the
     * prepares resolve later, when the area actually registers), and reply with
     * the configs the area must register against.
     */
    /**
     * Admit a DYNAMIC member (a runtime connector, a remote area host agent, an
     * area or gate in another process) on the strength of a join token: verify
     * the token against this cluster's secret, the joiner's role (its server
     * type), id and the token's expiry; record a host's area-type scope; then
     * prepare for its registration, pinned to the public key it presented.
     *
     * Validation is synchronous — a refused joiner throws here, before any
     * socket exists. The returned promise resolves when the member registers.
     */
    public admitMember(config: BaseServerConfig<any>, joinToken: unknown): Promise<RegisteredServerData<any>> {
        if(!this.config.clusterSecret) {
            throw new Error(`Master ${this.id} has no cluster secret, so it cannot verify join tokens (set servers.trust.keysFile, or let the Starter generate one).`);
        }
        const role = MasterServer.joinRoleFor(config?.type);
        if(!role) throw new Error(`Join refused: server type ${String(config?.type)} cannot join with a token.`);
        const claims = verifyJoinToken(this.config.clusterSecret, joinToken, { clusterId: this.clusterId, role, serverId: config.id });
        if(config.clusterId && config.clusterId !== this.clusterId) {
            throw new Error(`Join refused: '${config.id}' belongs to cluster '${config.clusterId}', not '${this.clusterId}'.`);
        }
        if(!isCurveKey(config.publicKey)) {
            throw new Error(`Join refused: '${config.id}' presented no valid public key — the key is what it will be pinned by.`);
        }
        if(role === 'host') {
            this.hostAdmissions.set(config.id, { areaTypes: [...(claims.areaTypes ?? [])] });
        }
        return this.prepareForServerRegistrationRequest({ ...config, clusterId: this.clusterId });
    }

    private static joinRoleFor(type: SERVER_TYPES | undefined): JoinRole | undefined {
        switch(type) {
            case HOST: return 'host';
            case CONNECTOR: return 'connector';
            case AREA: return 'area';
            case GATE: return 'gate';
            default: return undefined;
        }
    }

    /**
     * Admission scope per remote host, from its join token (`admitMember`). A
     * host with no entry was admitted by code in this process (unscoped).
     */
    readonly hostAdmissions : Map<string | number, { areaTypes: string[] }> = new Map();

    private hostScopeAllows(hostServerId: string | number, areaType: string): boolean {
        const scope = this.hostAdmissions.get(hostServerId);
        return !scope || scope.areaTypes.includes(areaType);
    }

    /**
     * AREA_PREPARE makes the master AND every in-scope connector trust a new
     * area. So it is honored only for an instance the master itself placed on
     * THIS host (or a rolled-back one it is about to reap), of a type in the
     * host's admission scope, at the host's own address, with a key. The host
     * cannot name an arbitrary config and have the cluster connect to it.
     */
    private validateAreaPrepare(host: RegisteredServerData<any>, areaConfig: BaseServerConfig<AreaServerConfigOptions>): BaseServerConfig<AreaServerConfigOptions> {
        const areaId = areaConfig?.id as string;
        const record = this.dynamicAreaTable.get(areaId);
        const rolledBack = this.rolledBackAreas.get(areaId);
        const placedHere = record ? `${record.hostServerId}` === `${host.id}` : (!!rolledBack && `${rolledBack.hostServerId}` === `${host.id}`);
        if(!placedHere) {
            throw new Error(`AREA_PREPARE refused: host '${host.id}' asked the cluster to trust '${areaId}', which the master did not place on it.`);
        }
        const areaType = record?.type ?? (typeof areaId === 'string' ? areaId.split(':')[0] : '');
        if(!this.hostScopeAllows(host.id, areaType)) {
            throw new Error(`AREA_PREPARE refused: area type '${areaType}' is outside host '${host.id}''s admitted scope.`);
        }
        if(areaConfig.host !== host.host) {
            throw new Error(`AREA_PREPARE refused: '${areaId}' claims host '${areaConfig.host}', but host '${host.id}' runs at '${host.host}'.`);
        }
        if(areaConfig.clusterId && areaConfig.clusterId !== this.clusterId) {
            throw new Error(`AREA_PREPARE refused: '${areaId}' belongs to cluster '${areaConfig.clusterId}'.`);
        }
        return { ...areaConfig, type: AREA, clusterId: this.clusterId };
    }

    private async handleAreaPrepare(host: RegisteredServerData<any>, requested: BaseServerConfig<AreaServerConfigOptions>) {
        const areaConfig = this.validateAreaPrepare(host, requested);
        // Kick off (not await — they resolve on the area's registration).
        this.prepareForServerRegistrationRequest(areaConfig).catch(err => {
            this.logger.error(`Master failed preparing for instance area ${areaConfig?.id}: ${err instanceof Error ? err.message : String(err)}`);
        });
        // Scope the dynamic-spawn fan-out: an instance area ("type:suffix") only reaches
        // connectors whose registered options.areas name its id OR its template type. A
        // connector with NO areas scoping receives everything (unchanged default) — the
        // filter is additive. Scoping rides in on the connector's registration options
        // (Starter.getBaseConnectorConfig threads config.area/areas → options.areas).
        // connectorScopeMatches is the shared id-or-type membership core (packages/server/
        // src/types.ts) — Starter.canConnectConnectorToArea uses the same core, wrapped in
        // its own extra area-side pin/serverOnly checks that have no meaning here.
        const areaId = areaConfig?.id;
        const areaType = (typeof areaId === 'string' && areaId.includes(':')) ? areaId.split(':')[0] : null;
        const inScope = (c: RegisteredServerData<ConnectorServerConfigOptions>) =>
            connectorScopeMatches(c.options?.areas, areaId, areaType);
        // Each descriptor carries the peer's public key + cluster: the area pins
        // the master and the connectors by these, exactly as they pin it by the
        // key in `areaConfig` (key distribution rides the prepare flow).
        const connectorConfigs = this.serversByType.connector.filter(inScope).map(c => ({
            id: c.id,
            host: c.host,
            port: c.port,
            type: CONNECTOR,
            options: c.options ?? {},
            publicKey: c.publicKey,
            clusterId: this.clusterId,
        }));
        await Promise.all(connectorConfigs.map(c => this.sendPrepareInstanceArea(c.id, areaConfig)));
        return {
            masterConfig: { id: this.config.id, host: this.config.host, port: this.config.port, type: MASTER, options: {}, publicKey: this.publicKey, clusterId: this.clusterId },
            connectorConfigs,
        };
    }

    /**
     * Send ONE connector the exact PREPARE_INSTANCE_AREA payload handleAreaPrepare fans out
     * to every in-scope connector at spawn time — the single send primitive both that
     * fan-out and introduceConnectorToInstance share, so a connector receives an identical
     * payload shape whether it existed at spawn time or arrived later.
     */
    private sendPrepareInstanceArea(connectorId: string | number, areaConfig: BaseServerConfig<AreaServerConfigOptions>) {
        return this.requestServer(connectorId, [MASTER_CONNECTOR_PROTOCOLS.PREPARE_INSTANCE_AREA, areaConfig]);
    }

    /**
     * Introduce a connector to a LIVE dynamic instance it never saw the PREPARE_INSTANCE_AREA
     * fan-out for — the connector was added (Starter.addConnectorAtRuntime) AFTER the
     * instance already spawned, so handleAreaPrepare's one-time, spawn-time fan-out never
     * reached it. Replays that same payload, built from the instance's CURRENT registration
     * (serversByType.areas) — no duplicate payload shape. A no-op if the instance isn't (or
     * is no longer) registered with this master.
     *
     * Only meaningful for instances THIS master knows about — i.e. only ever called by a
     * Starter that co-locates the master (`this.master` set). A Starter pointed at a REMOTE
     * master has no RPC to ask it to replay this introduction for instances it doesn't host
     * (would need a registration-replay endpoint on the master — out of scope; see the
     * task-8 report's documented limitation).
     */
    public async introduceConnectorToInstance(connectorId: string | number, areaId: string | number): Promise<void> {
        const areaConfig = this.serversByType.areas.find(a => a.id === areaId);
        if (!areaConfig) return;
        await this.sendPrepareInstanceArea(connectorId, areaConfig);
    }

    /**
     * A host (re)reported its registered types + live instances — refresh the
     * registry and adopt any instance we don't know (master-restart reconciliation).
     */
    public handleHostReport(hostServerId: string | number, report: HostReport) {
        // A token-admitted host may only advertise and run the area types its
        // token names; anything outside is dropped (and said so), not adopted.
        const advertised = report?.registeredTypes ?? [];
        const registeredTypes = advertised.filter(t => this.hostScopeAllows(hostServerId, t));
        if(registeredTypes.length !== advertised.length) {
            this.logger.error(`Host ${hostServerId} advertised area type(s) outside its admitted scope — ignored: ${advertised.filter(t => !registeredTypes.includes(t)).join(', ')}`);
        }
        const reportedInstances = report?.instances ?? [];
        const instances = reportedInstances.filter(r => this.hostScopeAllows(hostServerId, r.type));
        if(instances.length !== reportedInstances.length) {
            this.logger.error(`Host ${hostServerId} reported instance(s) of types outside its admitted scope — not adopted: ${reportedInstances.filter(r => !instances.includes(r)).map(r => r.areaId).join(', ')}`);
        }
        this.areaHostAgents.set(hostServerId, {
            registeredTypes,
            typeCapacities: report?.typeCapacities ?? {},
            typeDefaults: report?.typeDefaults ?? {},
            limits: report?.limits ?? {},
        });
        instances.forEach(record => {
            // A rolled-back spawn that completed late is a zombie — destroy, don't adopt.
            if (this.rolledBackAreas.has(record.areaId)) {
                this.reapZombieArea(record.areaId);
                return;
            }
            if (!this.dynamicAreaTable.get(record.areaId)) {
                const adopted = this.dynamicAreaTable.adopt({ ...record, hostServerId });
                // An adopted EMPTY ephemeral instance has nobody to trigger its
                // empty-teardown (e.g. its players were evicted during a partition)
                // — start its grace timer now so it can't leak.
                if (adopted.policy.ephemeral && memberCountOf(adopted) === 0) {
                    this.scheduleEmptyReap(adopted);
                }
            }
            // THE re-registration hook: this area is demonstrably alive on a
            // real host, so a destroy intent rehydrated for it may now be
            // applied. Runs for every reported instance, adopted or already
            // tracked (a report can arrive after the area registered).
            const live = this.dynamicAreaTable.get(record.areaId);
            if (live) {
                this.claimRehydratedIntent(live);
            }
        });
        this.syncInstances();
    }

    // ── Durable destroy intents: rehydration (INTENTS ONLY) ───────────────────

    /**
     * Read the destroy intents this master's predecessor left behind and hold
     * them PENDING. Called from `onAfterInit`, i.e. once `this.redis` exists —
     * in memory mode the store is a fresh Map and this is a no-op, which is
     * exactly today's behaviour.
     *
     * *** WHAT THIS DELIBERATELY DOES NOT DO: rehydrate anything else. ***
     * No area record, no area type, no topology is read from the store, here or
     * anywhere. What exists is what re-registers: `AreaHostAgent` re-reports
     * its running instances and `adopt()` ingests them, members included. A
     * stored record would be a second source of truth that can lie — after a
     * teardown-and-redeploy it would resurrect phantom areas no host will ever
     * back, and `isAreaLive` would route live players into them. So an intent
     * is a promise waiting for its area to prove it exists: applied only in
     * `claimRehydratedIntent`, aged out otherwise.
     */
    public async rehydrateDestroyIntents(): Promise<void> {
        let intents;
        try {
            intents = await this.stateStore.getDestroyIntents();
        } catch (err) {
            // A store that cannot be read must not stop a master from booting.
            this.logger.error(`Failed reading durable destroy intents: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
        intents.forEach(intent => {
            this.pendingRehydratedIntents.set(intent.areaId, { opts: intent.opts ?? {}, generation: intent.generation });
        });
        if (!this.pendingRehydratedIntents.size) return;
        this.logger.info(`Rehydrated ${this.pendingRehydratedIntents.size} pending destroy intent(s); each applies only once its area re-registers.`);
        this.armRehydratedIntentAgeOut();
    }

    /**
     * Nothing keeps an intent forever. Whatever has not been claimed by a
     * re-registering area within the age-out window is discarded from the
     * pending map AND deleted from the store — that is what stops a dead
     * deployment's leftovers from accumulating in a shared redis, and what
     * makes "the area never came back" a terminal state rather than a
     * permanently armed teardown.
     */
    private armRehydratedIntentAgeOut() {
        if (this.rehydratedIntentAgeOutTimer) return;
        const timer = setTimeout(() => {
            this.rehydratedIntentAgeOutTimer = undefined;
            const stale = [...this.pendingRehydratedIntents.keys()];
            this.pendingRehydratedIntents.clear();
            stale.forEach(areaId => {
                this.logger.warn(`Destroy intent for dynamic area ${areaId} expired: the area never re-registered.`);
                this.stateStore.clearDestroyIntent(areaId).catch(err => {
                    this.logger.error(`Failed clearing expired destroy intent for ${areaId}: ${err instanceof Error ? err.message : String(err)}`);
                });
            });
        }, this.rehydratedIntentAgeOutMs);
        (timer as any).unref?.();
        this.rehydratedIntentAgeOutTimer = timer;
    }

    /**
     * An area re-registered: if a rehydrated intent named it, re-arm the
     * `whenEmpty` destroy now (which, for an area that is already empty, tears
     * it down immediately — the same call the original request made).
     *
     * The generation check is what keeps an intent bound to the incarnation it
     * was armed against: a record that comes back OLDER than the intent is not
     * the area the intent was written for, so the intent stays pending (and
     * ages out) rather than destroying a different area that happens to share
     * an id.
     */
    private claimRehydratedIntent(record: DynamicAreaRecord) {
        const intent = this.pendingRehydratedIntents.get(record.areaId);
        if (!intent) return;
        if (record.generation < intent.generation) return;
        this.pendingRehydratedIntents.delete(record.areaId);
        this.logger.info(`Applying rehydrated destroy intent for dynamic area ${record.areaId} (generation ${record.generation} >= ${intent.generation}).`);
        this.destroyArea(record.areaId, { ...intent.opts, whenEmpty: true }).catch(err => {
            this.logger.error(`Failed applying rehydrated destroy intent for ${record.areaId}: ${err instanceof Error ? err.message : String(err)}`);
        });
    }

    /**
     * A serializable snapshot of the live instance topology for observability
     * (e.g. serve it from the admin web server's `onAdminGet`). Does not leak the
     * mutable member arrays.
     */
    public getDynamicAreasSnapshot() {
        const instances = this.dynamicAreaTable.list().map(r => ({
            areaId: r.areaId,
            type: r.type,
            gameId: r.gameId,
            state: r.state,
            generation: r.generation,
            hostServerId: r.hostServerId,
            memberCount: memberCountOf(r),
            ephemeral: r.policy.ephemeral,
        }));
        const countsByType: { [type: string]: number } = {};
        instances.forEach(i => { countsByType[i.type] = (countsByType[i.type] ?? 0) + 1; });
        return {
            total: instances.length,
            registeredTypes: this.areaTypes.types(),
            countsByType,
            instances,
            hosts: this.getAreaHostsSnapshot(),
        };
    }

    /** Broadcast the live instance topology to all areas (reuses the area fan-out). */
    public syncInstances() {
        const data: { [areaId: string]: PeerAreaData & { generation: number } } = {};
        this.dynamicAreaTable.list().forEach(r => {
            data[r.areaId] = {
                id: r.areaId,
                type: r.type,
                configData: r.configData,
                generation: r.generation,
            };
        });
        this.sendToServers(this.allAreaIds, [MASTER_AREA_PROTOCOLS.SYNC_DYNAMIC_AREA_DATA, data]);
    }

    private refreshAreaIds() {
        this.allAreaIds = this.serversByType.areas.map(a => a.id);
    }

    abstract onAuthenticateAdmin(payload: any) : Promise<boolean>;
    abstract onAdminPost(endpoint: string, payload: any, ctx: { adminId: string }) : Promise<any>;
    abstract onAdminGet(endpoint: string, payload: any) : Promise<any>;
    abstract onAreaMessage(areaId: string | number, message: { type: string | number, data: any }) : void;
    abstract onAreaRequest(areaId: string | number, request: { type: string | number, data: any }) : Promise<any>;

    public dispatchToAllAreas(message: MaybeSystemMessage) {
        const messageArray =  [message.type, message.data, message.to];
        this.sendToServers(this.allAreaIds, messageArray)
    }

    public updateGameMetaData(gameId: string, meta: any) {
        if(!this.gamesById[gameId]) {
            throw new Error(`Invalid gameId: ${gameId}`);
        }
        this.gamesById[gameId].meta = meta;
        if(this.serversByType.gate) {
            this.sendToServer(this.serversByType.gate.id, [MASTER_GATE_PROTOCOLS.UPDATE_GAME_META_DATA, gameId, meta])
        }
    }

    public addGameType(gameFile: ServerGameFile) {
        this.gameTypes[gameFile.type] = gameFile;
    }

    public async addGame(gameId: string | number, gameType: string | number, configData?: any) {
        this.logger.info(`Adding game: ${gameId}, gameType: ${gameType}, configData: ${configData}`);
        if(gameId in this.gamesById) {
            throw new Error(`Already added game: ${gameId}`)
        }
        const gameFile = this.gameTypes[gameType];
        if(!gameFile) {
            throw new Error(`Game type: ${gameType} does not exist.`);
        }
        if(!this.gamesByType[gameType]) {
            this.gamesByType[gameType] = {};
        }
        const meta = await makeGameFileMetaData(gameFile, gameId, configData);
        const gameData : PostConfigData = { id: gameId, meta, type: gameType, configData};
        this.gamesByType[gameType][gameId] = gameData
        this.gamesById[gameId] = gameData;
        // Every registered game has an area map from the moment it's added — even an
        // all-dynamic game with no static areas ever booted. Reservation/getInitialAreas
        // key into this map, so a missing entry would throw on the first join.
        if(!this.areaByGameMap[gameId]) {
            this.areaByGameMap[gameId] = {};
        }
        const { client: clientData } = await makeGameFileData(gameFile, gameId, configData, true);
        this.gameClientDataById[gameId] = clientData;
        if(this.serversByType.gate) {
            this.logger.debug(`SENDING TO GATE: [MASTER_GATE_PROTOCOLS.ADD_GAME, ${gameType}, ${gameId}, ${clientData}, ${meta}]`);
           // console.log('send to gate, [MASTER_GATE_PROTOCOLS.ADD_GAME, gameType, gameId, meta]');
            this.sendToServer(this.serversByType.gate.id, [MASTER_GATE_PROTOCOLS.ADD_GAME, gameType, gameId, clientData, meta, gameFile?.authIdentityPath])
        }
        this.logger.debug(`FINISHED ADDING GAME: gameId: ${gameId}, gameType: ${gameType}`);
    }
    public removeGame(gameId: string | number) {
        delete this.gamesById[gameId];
        if(this.serversByType.gate) {
            this.sendToServer(this.serversByType.gate.id, [MASTER_GATE_PROTOCOLS.REMOVE_GAME, gameId])
        }
    }
    public addGameArea(gameId: string, areaId: string | number) {
        this.logger.info(`Adding area: ${areaId} to game: ${gameId}`);
        if(!(areaId in this.serverConnections)) {
            this.logger.error(`no area id: ${areaId} in server connections: ${Object.keys(this.serverConnections)}`)
            throw new Error(`Area id ${areaId} did not exist.`);
        }
        if(!(this.areaByGameMap[gameId])) {
            this.areaByGameMap[gameId] = {};
        }
        this.areaByGameMap[gameId][areaId] = this.serverConnections[areaId];
        this.syncGameAreas(gameId);
        // this.logger.info(`FINISHED ADDING AREA: ${areaId} TO GAME: ${gameId}`);
    }

    public syncGlobalAreas() {
        const values = Object.values(this.globalAreaServers);
        const data : {[key: string]: PeerAreaData } = {};
        values.forEach(k => {
            data[k.id] = {
                id: k.id,
                type: k.options.areaFile.type,
                configData: k.options.areaConfigData,
            }
        });
        this.logger.debug(`Sent synced global area data to ${this.allAreaIds.length} areas.`);
        this.sendToServers(this.allAreaIds, [MASTER_AREA_PROTOCOLS.SYNC_GLOBAL_AREA_DATA, data]);
    }

    public syncGameAreas(gameId: string) {
        // Clear existing timeout for the gameId if it exists
        if (this.debounceMap.has(gameId)) {
          clearTimeout(this.debounceMap.get(gameId)!);
        }
      
        // Increment debounce count for this gameId
        const currentCount = (this.debounceCountMap.get(gameId) || 0) + 1;
        this.debounceCountMap.set(gameId, currentCount);
      
        // Schedule the debounce function
        this.debounceMap.set(
          gameId,
          setTimeout(() => {
            this.executeSyncGameAreas(gameId);
          }, this.DEBOUNCE_DELAY)
        );
      
        // If the debounce count exceeds the maximum, send immediately
        if (currentCount >= this.MAX_DEBOUNCES) {
          this.executeSyncGameAreas(gameId);
        }
      }
      
      private executeSyncGameAreas(gameId: string) {
        // Clear timeout and reset debounce count for the gameId
        if (this.debounceMap.has(gameId)) {
          clearTimeout(this.debounceMap.get(gameId));
          this.debounceMap.delete(gameId);
        }
        this.debounceCountMap.delete(gameId);
      
        // Logic to sync game areas
        const values = Object.values(this.areaByGameMap[gameId]);
        const data: { [key: string]: PeerAreaData } = {};
        const ids: (string | number)[] = [];
      
        values.forEach(k => {
          ids.push(k.id);
          data[k.id] = {
            id: k.id,
            type: k.options.areaFile.type,
            configData: k.options.areaConfigData,
          };
        });
      
        this.logger.debug(`Sent synced game: ${gameId} data to ${ids.length} areas.`);
        this.sendToServers(ids, [MASTER_AREA_PROTOCOLS.SYNC_GAME_AREA_DATA, data]);
      }

    public removeGameArea(gameId: string, areaId: string) {
        delete this.areaByGameMap[gameId][areaId];
        this.syncGameAreas(gameId);
    }

    private dispatchToAllAreasLocalOnly(message: MaybeSystemMessage) {
        this.allAreaIds.forEach(a => {
            this.localServers[a]?.onServerMessage(this.sharedConfig, message)
        });
    }
    
    public dispatchToArea(areaId: string, message: MaybeSystemMessage) {
        this.sendToServer(areaId, this.makeAreaMessagePayload(message));
    }

    public dispatchToAreas(areaIds: string[], message: MaybeSystemMessage) {
        this.sendToServers(areaIds, this.makeAreaMessagePayload(message));
    }

    /**
     * Areas parse master traffic as [MASTER_AREA_PROTOCOLS, ...] arrays — `to`
     * routes to a system's onMasterSystemMessage; no-`to` routes to the area
     * FILE's onGlobalMessage (mirrors requestArea's AREA_REQUEST shape). OMIT the
     * trailing `to` slot when absent — never send null/undefined over the wire.
     */
    private makeAreaMessagePayload(message: MaybeSystemMessage): ProtocolArray {
        const payload: ProtocolArray = [MASTER_AREA_PROTOCOLS.AREA_MESSAGE, message.type, message.data];
        if (message.to !== undefined) payload.push(message.to);
        return payload;
    }

    public broadcast(message: ReceivedMessage) {
        const payload = [SERVER_SERVER_PROTOCOLS.MASTER_BROADCAST, this.id, message.type, message.data];
        this.sendToServers(this.serverIds, payload);
    }

    // ── Unified Assets API: the master is the store + the change authority ──

    /** Optional audit hook — override in a MasterServer subclass for an audit trail. */
    protected onAssetUpdated(_change: AssetChange): void {}
    /** Optional audit hook — override in a MasterServer subclass for an audit trail. */
    protected onAssetRemoved(_change: AssetChange): void {}

    /** The single write chokepoint (area relay + admin both funnel here). */
    public async applyAssetWrite(namespace: AssetNamespace, path: string, bytes: Uint8Array, changedBy: AssetChangedBy): Promise<void> {
        await this.assetStore.write(namespace, path, bytes, 'application/json');
        const change: AssetChange = { namespace, path, byteSize: bytes.byteLength, changedBy };
        try { this.onAssetUpdated(change); } catch (e) { this.logger?.error?.(`onAssetUpdated hook threw: ${e instanceof Error ? e.message : String(e)}`); }
        this.fanOutAssetChange('updated', change);
    }

    public async applyAssetRemove(namespace: AssetNamespace, path: string, changedBy: AssetChangedBy): Promise<void> {
        const existing = await this.assetStore.read(namespace, path);
        const byteSize = existing?.bytes.byteLength ?? 0;
        await this.assetStore.remove(namespace, path);
        const change: AssetChange = { namespace, path, byteSize, changedBy };
        try { this.onAssetRemoved(change); } catch (e) { this.logger?.error?.(`onAssetRemoved hook threw: ${e instanceof Error ? e.message : String(e)}`); }
        this.fanOutAssetChange('removed', change);
    }

    /** Admin/author convenience: write a JSON value through the chokepoint. */
    public async saveAsset(namespace: AssetNamespace, path: string, value: unknown, changedBy: AssetChangedBy): Promise<void> {
        await this.applyAssetWrite(namespace, path, new TextEncoder().encode(JSON.stringify(value)), changedBy);
    }
    public async removeAsset(namespace: AssetNamespace, path: string, changedBy: AssetChangedBy): Promise<void> {
        await this.applyAssetRemove(namespace, path, changedBy);
    }

    /** Metadata-only fan-out: all areas; and, for client/ assets, all connectors → clients. */
    private fanOutAssetChange(action: AssetAction, change: AssetChange): void {
        this.sendToServers(this.allAreaIds, [MASTER_AREA_PROTOCOLS.ASSET_CHANGED, action, change]);
        if (change.namespace === 'client') {
            this.serversByType.connector.forEach(c =>
                this.sendToServer(c.id, [MASTER_CONNECTOR_PROTOCOLS.ASSET_CHANGED, action, change]));
        }
        for (const listener of [...this.assetChangeListeners]) {
            try { listener(action, change); } catch (e) {
                this.logger?.error?.(`asset change listener threw: ${e instanceof Error ? e.message : String(e)}`);
            }
        }
    }

    private readonly assetChangeListeners = new Set<(action: AssetAction, change: AssetChange) => void>();

    /**
     * Observe every asset change this master applies — admin writes, area
     * `$assets.save`/`remove` relays, everything through the
     * `applyAssetWrite`/`applyAssetRemove` chokepoint — after it is stored and
     * fanned out. Any number of listeners; returns the disposer. A throwing
     * listener is logged and never affects the write.
     */
    public addAssetChangeListener(listener: (action: AssetAction, change: AssetChange) => void): () => void {
        this.assetChangeListeners.add(listener);
        return () => { this.assetChangeListeners.delete(listener); };
    }

    /** area→master ASSET_WRITE (request/response). changedBy is derived from the connection. */
    public async handleAssetWrite(areaId: string | number, data: { op: 'save' | 'remove'; namespace: AssetNamespace; path: string; bytesB64?: string }): Promise<{ ok: true }> {
        if (data.namespace !== 'client' && data.namespace !== 'server') {
            throw new Error(`Invalid asset namespace "${data.namespace}" (must be 'client' or 'server').`);
        }
        const changedBy: AssetChangedBy = { type: 'area', areaId: String(areaId) };
        if (data.op === 'save') {
            const bytes = new Uint8Array(Buffer.from(data.bytesB64 ?? '', 'base64'));
            await this.applyAssetWrite(data.namespace, data.path, bytes, changedBy);
        } else {
            await this.applyAssetRemove(data.namespace, data.path, changedBy);
        }
        return { ok: true };
    }

    /** area→master ASSET_READ (request/response). */
    public async handleAssetRead(data: { namespace: AssetNamespace; path: string }): Promise<{ bytesB64: string; mimeType: string } | null> {
        if (data.namespace !== 'client' && data.namespace !== 'server') {
            throw new Error(`Invalid asset namespace "${data.namespace}" (must be 'client' or 'server').`);
        }
        const r = await this.assetStore.read(data.namespace, data.path);
        return r ? { bytesB64: Buffer.from(r.bytes).toString('base64'), mimeType: r.mimeType } : null;
    }
    
    private dispatchToAreasLocalOnly(areaIds: string[], message: MaybeSystemMessage) {
        areaIds.forEach(id => {
            this.localServers[id]?.onServerMessage(this.sharedConfig, message)
        });
    }
    
    public requestArea(areaId: string, payload: SystemRequest) : Promise<any> {
        // OMIT the trailing `to` slot when absent (never send null/undefined over
        // the wire) — the area routes `=== undefined` to onGlobalGameRequest.
        const request: ProtocolArray = [MASTER_AREA_PROTOCOLS.AREA_REQUEST, payload.type, payload.data];
        if (payload.to !== undefined) request.push(payload.to);
        return this.requestServer(areaId, request)
    }

    public getAvailableConnectorsWithArea(area: string) {
        return Object.values(this.serverConnections).filter(v => {
            const { clientCount, areas, maxClients } = v.heartbeats.lastState || {};
            return Number.isInteger(clientCount) && clientCount < maxClients && areas.includes(area);
        });
    }

    public override async onBeforeInit() {
        if(this.config.options?.disableAdminWebServer) {
            return;
        }
        return new Promise<void>((resolve, reject) => {
            /*
            if(this.httpServer) {
                this.httpServer.on('request', async (req, res) => {

                })
                return;
            }*/
            this.httpServer = http.createServer(async (req: any, res: any) => {
                res.setHeader('Access-Control-Allow-Origin', '*');
                res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
                res.setHeader('Access-Control-Allow-Headers', 'Content-Type, Authorization');

                const authToken = this.adminSessionFor(req.headers.authorization);
                const endpoint = req.url?.startsWith('/') ? req.url.substring(1) : req.url;
                const unauthorized = () => {
                    res.writeHead(401, { 'Content-Type': 'text/plain', 'WWW-Authenticate': 'Bearer' });
                    res.end('Unauthorized');
                };
                try {
                    if (req.method === 'OPTIONS') {
                        res.writeHead(200, {
                            'Content-Type': 'application/json',
                            'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
                            'Access-Control-Allow-Headers': 'Content-Type, Authorization'
                        });
                        res.end();
                        return;
                    }
                
                    if(req.method === 'POST') {
                        let body = '';
                        req.on('data', (chunk: any) => {
                            body += chunk;
                        });
                        req.on('end', async () => {
                            const data = tryParse(body);
                            try {
                                if(endpoint === MASTER_ENDPOINTS.AUTHENTICATE) {
                                    // A failed authentication ANSWERS (401) — it used to
                                    // leave the request hanging until the client gave up.
                                    const auth = await this.onAuthenticateAdmin(data);
                                    if(!auth) return unauthorized();
                                    this.pruneAdminSessions();
                                    const token = randomBytes(32).toString('base64url');
                                    const expiresAt = Date.now() + this.adminTokenTtlMs;
                                    this.adminSessions.set(token, expiresAt);
                                    res.writeHead(200, { 'Content-Type': 'application/json' });
                                    res.end(JSON.stringify({ token, expiresAt }));
                                } else if(authToken) {
                                    // The session token IS the authenticated admin id.
                                    const repsonseData = await this.onAdminPost(endpoint, data, { adminId: authToken });
                                    res.writeHead(200, { 'Content-Type': 'application/json' });
                                    res.end(JSON.stringify(repsonseData));
                                } else {
                                    unauthorized();
                                }
                            } catch (error) {
                                res.writeHead(400, { 'Content-Type': 'application/json' });
                                res.end(JSON.stringify({ message: error instanceof Error ? error.message : String(error) }));
                            }
                        });
                    } else if (req.method === 'GET') {
                        if(authToken) {
                            const repsonseData = await this.onAdminGet(endpoint, {});
                            res.writeHead(200, { 'Content-Type': 'application/json' });
                            res.end(JSON.stringify(repsonseData));
                            return;
                        } else {
                            unauthorized();
                        }
                    } else {
                        res.writeHead(405, { 'Content-Type': 'text/plain' });
                        res.end('Method Not Allowed');
                    }
                } catch (error) {
                    res.writeHead(400, { 'Content-Type': 'application/json' });
                    res.end(JSON.stringify({ message: error instanceof Error ? error.message : String(error) }));
                }
            });
            
            const port = this.config.options?.webPort || 3040;
            // Same rule as the gate's: `bindHost` when set, else every interface (unchanged).
            const onListening = () => {
                this.httpServer.off('error', reject);
                resolve();
            };
            // A failed listen (port taken, a bindHost this machine does not have)
            // fails the boot instead of leaving initialize() pending forever.
            this.httpServer.once('error', reject);
            if(this.config.bindHost) {
                this.httpServer.listen(port, this.config.bindHost, onListening);
            } else {
                this.httpServer.listen(port, onListening);
            }
        })
    }


    /**
     * Runs AFTER `BaseServer.initialize()` created `this.redis`, which is the
     * earliest point a redis-backed store exists — the same ordering constraint
     * `GateServer.stateStore` documents. A subclass that overrides this must
     * call `super.onAfterInit()`, or its master boots with its predecessor's
     * destroy intents stranded in the store until they age out.
     */
    public override async onAfterInit() {
        // ONE LINE, for the same reason the gate logs one (see
        // `GateServer.onAfterInit`): the worst failure this feature has is a
        // SILENT DOWNGRADE, and on the master it is quieter still — a
        // memory-mode master looks completely healthy right up until a restart,
        // when its durable destroy intents turn out never to have been durable
        // and its member spill turns out to have been the heap. The prefix is in
        // the line because a wrong prefix is the same failure in disguise.
        this.logger.info(this.redis
            ? `state store: redis (prefix=${this.config.options?.redisKeyPrefix ?? DEFAULT_MASTER_REDIS_KEY_PREFIX}) — dynamic-area members spill to redis and destroy intents survive a restart`
            : `state store: memory (dynamic-area members live in this process' heap; destroy intents do NOT survive a restart)`);
        await this.rehydrateDestroyIntents();
    }

    public override async close() {
			this.clearAllResources();
			await super.close();
			if(!this.httpServer) return;
			return new Promise<void>((resolve) => {
				this.httpServer.close(resolve);
				this.clearAllResources();
				delete this.httpServer;
			})
    }

    private clearAllResources() {
			this.debounceMap?.forEach((timeout, gameId) => {
				clearTimeout(timeout);
			});
			this.debounceMap?.clear();
			this.debounceCountMap?.clear();
			this.emptyReapTimers?.forEach((timer) => clearTimeout(timer));
			this.emptyReapTimers?.clear();
			this.lostHostPrepareTimers?.forEach((timer) => clearTimeout(timer));
			this.lostHostPrepareTimers?.clear();
			this.pendingWhenEmptyDestroys?.forEach(({ timer }) => clearTimeout(timer));
			this.pendingWhenEmptyDestroys?.clear();
			// The DURABLE intents are deliberately NOT cleared from the store
			// here — surviving this process is their entire purpose. Only the
			// in-process pending map + its age-out timer die with the master.
			if(this.rehydratedIntentAgeOutTimer) {
				clearTimeout(this.rehydratedIntentAgeOutTimer);
				this.rehydratedIntentAgeOutTimer = undefined;
			}
			this.pendingRehydratedIntents?.clear();
			this.lostAreaHostConfigs?.clear();
        // Add any other necessary cleanup logic here
    }

    private async onGetInitialAreas(gameId: string, clientData: { id: string,  auth: any, options?: any }) : Promise<Array<string | number | AreaIdAndOptions>> {
        //console.log("onGetInitialAreas", { gameId, clientData })
        const game = this.gamesById[gameId];
        //console.log("game was:", game)
        if(!game) throw new Error(`Game id: ${gameId} did not exist`);
        const file = this.gameTypes[game.type];
        const items = await file.getInitialAreas({ 
            id: clientData.id, 
            auth: clientData.auth, 
            options: clientData.options 
        }, {
            meta: game.meta,
            requestArea: this.requestServer.bind(this),
        }, Object.keys(this.areaByGameMap[gameId]));

        if(!Array.isArray(items)) {
            return [items];
        } else {
            return items;
        }
    }
    

    /**
     * Whether `areaId` is currently a live, routable area for `gameId`.
     * dynamicAreaTable is the control-plane authority for a DYNAMIC instance
     * id: if a record exists, its `state` decides — READY is live; DRAINING
     * (destruction-committed — its connection can still be up while it drains
     * out existing members) and CLOSED (already removed from the table) are
     * not, regardless of whether a connection is still registered. Only when
     * there is no dynamicAreaTable record at all (a plain STATIC area) do we
     * fall back to the registered-connection bookkeeping (game-scoped or
     * global) that the create/missing-area checks in finishReservationForAreas
     * already trust. Shared by the rejoin filter below and
     * finishReservationForAreas so both agree on what "live" means.
     */
    private isAreaLive(gameId: string, areaId: string | number): boolean {
        const dynamic = this.dynamicAreaTable.get(String(areaId));
        if(dynamic) {
            return dynamic.state === DYNAMIC_AREA_STATE.READY;
        }
        return areaId in this.areaByGameMap[gameId] || areaId in this.globalAreaServers;
    }

    /**
     * Turns a resolved area-id list — getInitialAreas's result, or the
     * surviving subset of a rejoining client's previous areas — into
     * handleFinishedReservation's reply: drops empties, create-or-joins any
     * DYNAMIC-TYPE target, validates every remaining id is live, and returns
     * the final list. Shared so the rejoin fast path and the normal
     * getInitialAreas path funnel through one reply-shaping tail instead of
     * two copies of it. The liveness check here is intentionally a superset of
     * isAreaLive (see below) — it must accept anything the normal, non-rejoin
     * path accepted before rejoin routing existed.
     */
    private async finishReservationForAreas(gameId: string, areas: Array<string | number | AreaIdAndOptions>) {
        let r = areas;
        this.logger.debug(`Valid areas for game before removing empty values were: ${r.join(', ')}`);
        r = r?.filter(rr => rr !== null && rr !== undefined && rr !== "");

        if(r === null || r === undefined || !r?.length) {
            throw new Error(`Could not finish reservation.`);
        }

        this.logger.debug(`Valid areas for game: ${gameId} after removing empty values were: ${r.join(', ')}`);

        // Resolve DYNAMIC-area targets returned by getInitialAreas: an item whose
        // areaId is a registered dynamic TYPE (optionally carrying a matchmaking
        // `key`) and is not already a live area is create-or-joined into a concrete
        // area right here. getInitialAreas is the authoritative gate (it ran with
        // the client's auth and CHOSE this area+key), so the area server just
        // creates it — no further permission needed. createOrJoinArea validates the
        // type is registered and there is capacity, or throws.
        for(let i = 0; i < r.length; i++) {
            const item: any = r[i];
            const isObj = item !== null && typeof item === 'object';
            const rawId = isObj ? item.areaId : item;
            const key = isObj ? item.key : undefined;
            const isLive = rawId in this.areaByGameMap[gameId] || rawId in this.globalAreaServers;
            if(!isLive && (this.areaTypes.has(String(rawId)) || key !== undefined)) {
                const { areaId } = await this.createOrJoinArea(String(rawId), { areaKey: key ?? String(rawId), gameId, createdBy: 'master' });
                if(isObj) { item.areaId = areaId; delete item.key; }
                else { r[i] = areaId; }
            }
        }

        const missingAreas = r.filter(area => {
            let areaId : string | number = "";
            if(typeof area === "object") {
                if(area === null || !("areaId" in area)) {
                    //this.logger.debug(`handleFinishedReservation: Found invalid area object: ${JSON.stringify(area)}`);
                    return true;
                }
                areaId = area.areaId;
            } else {
                areaId = area;
            }
            if(areaId === "") {
                // this.logger.debug(`handleFinishedReservation: Found invalid areaId: ${areaId}`);
                return true
            }
            // this.logger.debug(`handleFinishedReservation: Checking if areaId: ${areaId} is live for game: ${gameId}.`);
            // Deliberately ADDITIVE here, not a bare `!isAreaLive`: isAreaLive is
            // stricter than the check this tail used before rejoin routing
            // existed (a DRAINING or still-PENDING dynamic instance is "not
            // READY" but can still have a registered connection). The rejoin
            // pre-filter above (`surviving = rejoinAreaIds.filter(isAreaLive)`)
            // must stay strict — it guards against routing a *reconnecting*
            // client into an area that is dying. This tail instead guards
            // against garbage/unregistered ids on every path (rejoin and
            // non-rejoin alike), so it must accept anything the pre-isAreaLive
            // membership check always accepted, in addition to anything
            // isAreaLive accepts — a superset, never a narrower check.
            return !(this.isAreaLive(gameId, areaId) ||
                areaId in this.areaByGameMap[gameId] || areaId in this.globalAreaServers);
        })
        if(missingAreas.length) {
            this.logger.debug(`handleFinishedReservation: Found: ${missingAreas.length} missing areas for game: ${gameId}. They were: ${missingAreas.join(', ')}`);
        } else {
            this.logger.debug(`handleFinishedReservation: No missing areas for game: ${gameId}`);
        }
        if(missingAreas.length) {
            throw new Error(`Could not finish reservation, areas: ${missingAreas.join(', ')} were not valid areas registered with the master server.`)
        }

        // console.log('finish reservation was', r);
        return r;
    }

    private async handleFinishedReservation(clientId: string, gameId: string, auth: any, options?: any, rejoinAreaIds?: Array<string | number>) {
       //console.log('handleFinishedReservation', { clientId, gameId, auth, options })

        // A reconnecting client is routed back into the areas it actually holds
        // a seat in, instead of through the game's getInitialAreas (which could
        // reshuffle a stable session into a fresh spawn/matchmaking decision).
        // That list is derived by the GATE from server state (the client's
        // recorded area membership, matched to its verified auth identity) and
        // arrives on the SEAT — never from `options`, which the client can
        // write: `options` is the raw client join body merged with whatever the
        // client sent on its own GET_FIRST_AREAS ping. A client naming an area
        // it holds no seat in gets nothing here; it takes the normal
        // getInitialAreas path like anyone else. Only when NONE of its held
        // areas survived (e.g. they were all ephemeral dynamic instances torn
        // down while it was disconnected) do we fall through to that path too.
        if(Array.isArray(rejoinAreaIds) && rejoinAreaIds.length) {
            const surviving = rejoinAreaIds.filter((areaId) => this.isAreaLive(gameId, areaId));
            if(surviving.length) {
                this.logger.debug(`handleFinishedReservation: Rejoining client ${clientId} back into held areas: ${surviving.join(', ')}`);
                return this.finishReservationForAreas(gameId, surviving);
            }
            this.logger.debug(`handleFinishedReservation: Rejoining client ${clientId} held no surviving areas; falling back to getInitialAreas.`);
        }

        this.logger.debug(`handleFinishedReservation: The available areaIds for the provided game were: ${Object.keys(this.areaByGameMap[gameId])}`);

       this.logger.debug(`handleFinishedReservation: clientId: ${clientId}, gameId: ${gameId}, auth: ${JSON.stringify(auth)}, options: ${JSON.stringify(options)}`);
       this.logger.debug(`handleFinishedReservation: The available areas for the provided game were: ${Object.keys(this.areaByGameMap[gameId])}`);
        const r = await this.onGetInitialAreas(gameId, { id: clientId, auth, options });
        return this.finishReservationForAreas(gameId, r);
    }

    /*
    private async handleAreaRelay(toAreaId: string, fromAreaId: string, to: string | number, type: string | number, data: any) {
                        // toAreaId, fromAreaId, to, type, data
        return 
    }*/

    public async onServerRequest(server: RegisteredServerData<any>, payload: any): Promise<any> {
        if(server.type === AREA) {
            switch(payload[0]) {
                case MASTER_AREA_PROTOCOLS.CREATE_AREA:
                    // payload: [proto, type, opts] — the requesting area owns it.
                    return this.createArea(payload[1], { ...(payload[2] ?? {}), createdBy: server.id });
                case MASTER_AREA_PROTOCOLS.DESTROY_AREA:
                    // payload: [proto, areaId, opts] — only the owner (or master) may.
                    return this.destroyArea(payload[1], { ...(payload[2] ?? {}), requesterId: server.id });
                case MASTER_AREA_PROTOCOLS.ASSET_WRITE:
                    return this.handleAssetWrite(server.id, payload[1]);
                case MASTER_AREA_PROTOCOLS.ASSET_READ:
                    return this.handleAssetRead(payload[1]);
            }
            return this.onAreaRequest(server.id, { type: payload[0], data: payload[1] });
        } else if (server.type === HOST) {
            switch(payload[0]) {
                case MASTER_AREA_PROTOCOLS.AREA_PREPARE:
                    return this.handleAreaPrepare(server, payload[1]);
            }
            return;
        } else if (server.type === GATE) {
            // Gate-side matchmaking relay — authorized because it arrives over the
            // registered gate connection (same trust as seat reservation).
            switch(payload[0]) {
                case MASTER_GATE_PROTOCOLS.CREATE_DYNAMIC_AREA:
                    return this.createOrJoinArea(payload[1], payload[2]);
                case MASTER_GATE_PROTOCOLS.DESTROY_DYNAMIC_AREA:
                    return this.tearDownDynamicArea(payload[1], payload[2]);
                case MASTER_GATE_PROTOCOLS.GET_AREA_CAPACITY:
                    return this.getAreaCapacity(payload[1]);
                case MASTER_GATE_PROTOCOLS.LIST_DYNAMIC_AREAS:
                    return this.listDynamicAreas(payload[1]);
            }
            return;
        } else if (server.type === CONNECTOR) {
            //console.log('ON SERVER REQ CONNECTOR', payload);
            switch(payload[0]) {
                case CONNECTOR_MASTER_REQUEST_PROTOCOLS.FINISHED_RESERVATION_GET_INITIAL_AREAS:
                    // console.log('handleFinishedReservation')
                    //console.log('CONNECTOR_MASTER_REQUEST_PROTOCOLS.FINISHED_RESERVATION_GET_INITIAL_AREAS')
                    // client.privateId, seat.gameId, seat.auth, merged options,
                    // seat.rejoinAreaIds (gate-derived — the ONLY rejoin source)
                    return this.handleFinishedReservation(payload[1], payload[2], payload[3], payload[4], payload[5]);
                case CONNECTOR_MASTER_REQUEST_PROTOCOLS.AREA_RELAY: {
                    // [AREA_RELAY, toAreaId, fromAreaId, type, data, to?] — the
                    // connector's relay order, `to` trailing so a game-hook message
                    // OMITS the slot entirely (reads back as undefined).
                    const toAreaId = payload[1];
                    const fromAreaId = payload[2];
                    const type = payload[3];
                    const data = payload[4];
                    const toSystem = payload[5];
                    // OMIT the trailing `to` slot when absent (never send
                    // null/undefined over the wire) — the receiving area routes
                    // `=== undefined` to the area file's game hook.
                    const relay: ProtocolArray = [MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE, fromAreaId, type, data];
                    if (toSystem !== undefined) relay.push(toSystem);
                    return this.requestServer(toAreaId, relay)
                }
            }
        }
    }

    onServerMessage(server: RegisteredServerData<any>, payload: any) {
        if(server.type === AREA) {
            switch(payload[0]) {
                case MASTER_AREA_PROTOCOLS.AREA_MESSAGE:
                    this.onAreaMessage(server.id, { type: payload[1], data: payload[2] });
                    return;
                case MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE: {
                    // Re-forward preserving the omitted-`to` contract: only append
                    // the trailing slot when the sender included one.
                    const relay: ProtocolArray = [MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE, server.id, payload[2], payload[3]];
                    if (payload[4] !== undefined) relay.push(payload[4]);
                    return this.sendToServer(payload[1], relay)
                }
                case MASTER_AREA_PROTOCOLS.AREA_MEMBERSHIP:
                    // payload: [proto, clientId, joined] — the sending area IS the instance.
                    return this.notifyAreaMembership(server.id as string, payload[1], payload[2]);
            }
        } else if (server.type === HOST) {
            switch(payload[0]) {
                case MASTER_AREA_PROTOCOLS.HOST_REPORT_AREAS:
                    return this.handleHostReport(server.id, payload[1]);
            }
        } else if (server.type === CONNECTOR) {
            switch(payload[0]) {
                case CONNECTOR_MASTER_REQUEST_PROTOCOLS.AREA_RELAY: {
                     // [AREA_RELAY, toAreaId, fromAreaId, type, data, to?] — same
                     // trailing-optional-`to` order as the request branch above.
                     const toAreaId = payload[1];
                     const fromAreaId = payload[2];
                     const type = payload[3];
                     const data = payload[4];
                     const toSystem = payload[5];
                    // OMIT the trailing `to` slot when absent — see the AREA_RELAY
                    // request branch above.
                    const relay: ProtocolArray = [MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE, fromAreaId, type, data];
                    if (toSystem !== undefined) relay.push(toSystem);
                    return this.sendToServer(toAreaId, relay)
                }
            }
        }
    }
}